(() => {
  'use strict';

  const C = window.ArelioConfig || {};
  const S = C.strings || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const store = {
    get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {} },
    sget(key) { try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; } },
    sset(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) {} }
  };

  const escapeHtml = (str) => String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const debounce = (fn, wait = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };
  const sized = (url, w) => (url ? url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : '');
  const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (m, k) => (values[k] != null ? values[k] : m));

  function formatMoney(cents, format) {
    const fmt = format || C.moneyFormat || '${{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : Math.round(cents || 0);
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    };
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return withDelimiters(value, 2);
    let out;
    switch (match[1]) {
      case 'amount_no_decimals': out = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': out = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelimiters(value, 2, "'", '.'); break;
      case 'amount_with_space_separator': out = withDelimiters(value, 2, ' ', ','); break;
      default: out = withDelimiters(value, 2);
    }
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }

  function track(name, data = {}) {
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('arelio_' + name, data); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'arelio_' + name }, data)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('arelio:' + name, { detail: data }));
  }

  let toastTimer;
  function toast(message, type = 'info') {
    const el = $('[data-ac-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('ac-toast--error', type === 'error');
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }

  async function fetchJSON(url, options = {}) {
    const res = await fetch(url, Object.assign({ headers: { Accept: 'application/json' } }, options));
    let data = null;
    try { data = await res.json(); } catch (e) {}
    if (!res.ok || (data && data.status && data.status >= 400)) {
      const msg = (data && (data.description || data.message)) || S.error || 'Something went wrong';
      throw new Error(msg);
    }
    return data;
  }

  async function fetchHTML(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(res.statusText);
    const text = await res.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  /* ---------- Overlays: drawers, modals, search ---------- */
  const Overlay = {
    stack: [],
    lastFocus: new Map(),
    open(el, trigger) {
      if (!el || el.classList.contains('is-open')) return;
      this.lastFocus.set(el, trigger || document.activeElement);
      el.hidden = false;
      el.setAttribute('aria-hidden', 'false');
      void el.offsetWidth; /* start the open transition from the closed state */
      el.classList.add('is-open');
      this.stack.push(el);
      document.body.classList.add('ac-scroll-lock');
      const focusTarget = $('[data-autofocus]', el) || $('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', el);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
      el.dispatchEvent(new CustomEvent('overlay:open'));
    },
    close(el) {
      el = el || this.stack[this.stack.length - 1];
      if (!el || !el.classList.contains('is-open')) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      this.stack = this.stack.filter((x) => x !== el);
      if (!this.stack.length) document.body.classList.remove('ac-scroll-lock');
      const back = this.lastFocus.get(el);
      if (back && back.focus) back.focus({ preventScroll: true });
      el.dispatchEvent(new CustomEvent('overlay:close'));
    },
    init() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-open]');
        if (opener) {
          const target = document.getElementById(opener.getAttribute('data-open'));
          if (target) { e.preventDefault(); this.open(target, opener); }
          return;
        }
        const closer = e.target.closest('[data-close]');
        if (closer) {
          const overlay = closer.closest('.ac-drawer, .ac-modal, .ac-search');
          if (overlay) { e.preventDefault(); this.close(overlay); }
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.stack.length) this.close();
        if (e.key === 'Tab' && this.stack.length) {
          const el = this.stack[this.stack.length - 1];
          const focusables = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el).filter((n) => n.offsetParent !== null);
          if (!focusables.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }
  };

  /* ---------- UTM capture ---------- */
  const UTM = {
    keys: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'ttclid', 'gclid'],
    capture() {
      if (!C.utm) return;
      const params = new URLSearchParams(location.search);
      const found = {};
      this.keys.forEach((k) => { if (params.get(k)) found[k] = params.get(k).slice(0, 120); });
      if (!Object.keys(found).length) return;
      found.landing_page = location.pathname.slice(0, 120);
      found.captured_at = new Date().toISOString().slice(0, 16);
      if (!store.get('ac_utm_first')) store.set('ac_utm_first', found);
      store.set('ac_utm_last', found);
      store.sset('ac_utm_sent', false);
    },
    async attach() {
      if (!C.utm || store.sget('ac_utm_sent')) return;
      const last = store.get('ac_utm_last');
      const first = store.get('ac_utm_first');
      if (!last && !first) return;
      const attributes = {};
      if (last) Object.keys(last).forEach((k) => { attributes[k] = last[k]; });
      if (first && first.utm_source) attributes.first_utm_source = first.utm_source;
      if (first && first.utm_campaign) attributes.first_utm_campaign = first.utm_campaign;
      try {
        await fetchJSON(C.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
        store.sset('ac_utm_sent', true);
      } catch (e) {}
    }
  };

  /* ---------- Confetti ---------- */
  function confetti() {
    if (!C.confetti || reduceMotion) return;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:200';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = innerWidth * dpr; canvas.height = innerHeight * dpr; ctx.scale(dpr, dpr);
    const colors = ['#537e74', '#354a43', '#e7a52b', '#ffffff', '#9caf88'];
    const originX = isMobile() ? innerWidth / 2 : innerWidth - 220;
    const parts = Array.from({ length: 110 }, () => ({ x: originX, y: 120, vx: (Math.random() - 0.5) * 12, vy: Math.random() * -9 - 3, s: Math.random() * 6 + 4, r: Math.random() * 6, c: colors[(Math.random() * colors.length) | 0] }));
    const start = performance.now();
    (function frame(now) {
      const t = now - start;
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      parts.forEach((p) => { p.vy += 0.32; p.x += p.vx; p.y += p.vy; p.r += 0.12; ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.c; ctx.globalAlpha = Math.max(0, 1 - t / 1700); ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6); ctx.restore(); });
      if (t < 1700) requestAnimationFrame(frame); else canvas.remove();
    })(start);
  }

  /* ---------- Cart ---------- */
  const Cart = {
    state: null,
    upsellCache: new Map(),
    productCache: new Map(),
    drawer: null,
    sectionIds() {
      const ids = ['ac-cart-drawer'];
      const page = $('[data-cart-page]');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },
    open() { if (this.drawer) Overlay.open(this.drawer); },
    async post(endpoint, payload) {
      payload.sections = this.sectionIds().join(',');
      payload.sections_url = location.pathname;
      return fetchJSON(endpoint + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
    },
    async add(items, opts = {}) {
      await UTM.attach();
      const before = this.state;
      const data = await this.post(C.routes.cartAdd, { items });
      if (data.sections) this.render(data.sections);
      await this.refresh(before);
      if (opts.open !== false) this.open();
      const count = items.reduce((n, i) => n + (i.quantity || 1), 0);
      track('add_to_cart', { items: items.map((i) => ({ variant_id: i.id, quantity: i.quantity })), quantity: count, source: opts.source || 'theme' });
      return data;
    },
    async change(line, quantity) {
      const before = this.state;
      const data = await this.post(C.routes.cartChange, { line, quantity });
      if (data.sections) this.render(data.sections);
      this.state = data;
      this.afterChange(before);
      return data;
    },
    async update(payload) {
      const before = this.state;
      const data = await this.post(C.routes.cartUpdate, payload);
      if (data.sections) this.render(data.sections);
      this.state = data;
      this.afterChange(before);
      return data;
    },
    async refresh(before) {
      this.state = await fetchJSON(C.routes.cart + '.js');
      this.afterChange(before);
      return this.state;
    },
    afterChange(before) {
      const cart = this.state;
      if (!cart) return;
      $$('[data-cart-count]').forEach((el) => {
        const prev = el.textContent;
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
        if (prev !== el.textContent) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
      });
      const qualifying = this.qualifyingTotal(cart);
      const prevTotal = before ? this.qualifyingTotal(before) : qualifying;
      const ship = (C.freeShipping || 0) * this.rate();
      const gift = (C.giftThreshold || 0) * this.rate();
      if (before && ((ship && prevTotal < ship && qualifying >= ship) || (C.giftEnabled && gift && prevTotal < gift && qualifying >= gift))) confetti();
      this.enforceRules(cart);
      document.dispatchEvent(new CustomEvent('arelio:cart-updated', { detail: cart }));
    },
    rate() { return (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1; },
    /* Gift products: the _gift property, the gift setting, or product type "Gift". Add-on products: the _addon_for property or product type "Add-on". */
    isGift(item) {
      if (item.properties && (item.properties._gift === 'true' || item.properties._gift === true)) return true;
      if (/^gift$/i.test(item.product_type || '')) return true;
      return (C.giftProductIds || []).some((id) => String(id) === String(item.product_id));
    },
    addonParent(item) { return item.properties && item.properties._addon_for ? String(item.properties._addon_for) : null; },
    isAddon(item) { return !!this.addonParent(item) || /^add-?on$/i.test(item.product_type || ''); },
    /* What counts toward free shipping and the gift: everything except gifts and the shipping protection fee. */
    qualifyingTotal(cart) {
      return cart.items.reduce((sum, item) => (this.isGift(item) || item.variant_id === C.protectionVariantId ? sum : sum + item.final_line_price), 0);
    },
    /* One add-on (quantity 1) per main product in the cart, then one free gift (quantity 1) if what's left is over the gift amount. */
    async enforceRules(cart) {
      if (this.enforcing) return;
      const updates = {};
      const addonKept = new Set();
      let qualifying = 0;
      cart.items.forEach((item) => {
        if (this.isGift(item) || item.variant_id === C.protectionVariantId) return;
        if (this.isAddon(item)) {
          const parent = this.addonParent(item);
          const hasParent = !!parent && cart.items.some((i) => String(i.product_id) === parent && !this.isGift(i) && !this.isAddon(i));
          if (!hasParent || addonKept.has(parent)) { updates[item.key] = 0; return; }
          addonKept.add(parent);
          if (item.quantity > 1) { updates[item.key] = 1; qualifying += Math.round(item.final_line_price / item.quantity); return; }
        }
        qualifying += item.final_line_price;
      });
      const dropGifts = !C.giftEnabled || qualifying < (C.giftThreshold || 0) * this.rate();
      let giftDropped = false;
      let giftKept = false;
      cart.items.forEach((item) => {
        if (!this.isGift(item)) return;
        if (dropGifts) { updates[item.key] = 0; giftDropped = true; }
        else if (giftKept) updates[item.key] = 0;
        else { giftKept = true; if (item.quantity > 1) updates[item.key] = 1; }
      });
      if (!Object.keys(updates).length) return;
      this.enforcing = true;
      try {
        await this.update({ updates });
        if (giftDropped) toast(S.giftRemoved || 'Your free gift was removed because the cart is below the gift amount.');
      } catch (e) {}
      this.enforcing = false;
    },
    render(sections) {
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-cart-inner]', doc);
        const host = id === 'ac-cart-drawer' ? this.drawer : $(`[data-cart-page][data-section-id="${id}"]`);
        const current = host && $('[data-cart-inner]', host);
        if (!fresh || !current) return;
        const oldFill = $('[data-progress-fill]', current);
        const oldP = oldFill ? oldFill.style.getPropertyValue('--p') : null;
        const scroll = $('.ac-drawer__body', host) ? $('.ac-drawer__body', host).scrollTop : 0;
        current.innerHTML = fresh.innerHTML;
        const newFill = $('[data-progress-fill]', current);
        if (newFill && oldP) {
          const target = newFill.style.getPropertyValue('--p');
          newFill.style.setProperty('--p', oldP);
          requestAnimationFrame(() => requestAnimationFrame(() => newFill.style.setProperty('--p', target)));
        }
        const body = $('.ac-drawer__body', host);
        if (body) body.scrollTop = scroll;
        this.hydrate(current);
      });
    },
    hydrate(root) {
      const isDrawer = root === this.drawer || (this.drawer && this.drawer.contains(root));
      if (isDrawer && !this.drawer.classList.contains('is-open')) return;
      this.loadUpsells(root);
    },
    async loadUpsells(root) {
      const box = $('[data-cart-upsells]', root);
      if (!box) return;
      const pid = box.dataset.productId;
      if (!pid) return;
      const key = pid + ':' + (box.dataset.exclude || '');
      if (this.upsellCache.has(key)) { this.fillUpsells(box, this.upsellCache.get(key)); return; }
      try {
        const url = `${C.routes.recommendations}?section_id=ac-cart-upsells&product_id=${encodeURIComponent(pid)}&limit=8&intent=related`;
        const doc = await fetchHTML(url);
        const inner = $('[data-upsells-inner]', doc);
        const html = inner ? inner.innerHTML : '';
        this.upsellCache.set(key, html);
        this.fillUpsells(box, html);
      } catch (e) {}
    },
    fillUpsells(box, html) {
      const holder = $('[data-upsells-track]', box);
      if (!holder || !html) return;
      const exclude = (box.dataset.exclude || '').split(',').filter(Boolean);
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      $$('[data-upsell]', tmp).forEach((n) => { if (exclude.includes(n.dataset.productId)) n.remove(); });
      const items = $$('[data-upsell]', tmp).slice(0, 6);
      if (!items.length) return;
      holder.innerHTML = '';
      items.forEach((n) => holder.appendChild(n));
      box.hidden = false;
      initCarousels(box);
    },
    async productJSON(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      const data = await fetchJSON(`${C.routes.root.replace(/\/$/, '')}/products/${handle}.js`);
      this.productCache.set(handle, data);
      return data;
    },
    async swapVariant(lineEl) {
      const line = parseInt(lineEl.dataset.line, 10);
      const item = this.state && this.state.items[line - 1];
      if (!item) return;
      const options = $$('[data-line-option]', lineEl).map((s) => s.value);
      const product = await this.productJSON(item.handle);
      const match = product.variants.find((v) => v.options.every((o, i) => o === options[i]));
      if (!match || match.id === item.variant_id) return;
      if (!match.available) { toast(S.unavailable || 'That option is unavailable', 'error'); this.render({}); return; }
      const existing = this.state.items.filter((i, idx) => i.variant_id === match.id && idx !== line - 1 && !Object.keys(i.properties || {}).length).reduce((n, i) => n + i.quantity, 0);
      const updates = {};
      updates[item.key] = 0;
      updates[match.id] = existing + item.quantity;
      lineEl.classList.add('is-loading');
      try { await this.update({ updates }); } catch (e) { toast(e.message, 'error'); lineEl.classList.remove('is-loading'); }
    },
    async toggleProtection(checked) {
      const vid = C.protectionVariantId;
      if (!vid) return;
      const cart = this.state || (await this.refresh());
      const index = cart.items.findIndex((i) => i.variant_id === vid);
      try {
        if (checked && index === -1) {
          const data = await this.post(C.routes.cartAdd, { items: [{ id: vid, quantity: 1 }] });
          if (data.sections) this.render(data.sections);
          await this.refresh(cart);
          track('protection_added', {});
        } else if (!checked && index > -1) {
          await this.change(index + 1, 0);
          track('protection_removed', {});
        }
      } catch (e) { toast(e.message, 'error'); }
    },
    init() {
      this.drawer = $('#CartDrawer');
      if (!this.drawer) return;
      this.drawer.addEventListener('overlay:open', () => this.loadUpsells(this.drawer));
      this.refresh().then(() => {
        this.hydrate(this.drawer);
        const page = $('[data-cart-page]');
        if (page) this.hydrate(page);
      }).catch(() => {});
      document.addEventListener('click', async (e) => {
        const plus = e.target.closest('[data-qty-plus], [data-qty-minus]');
        if (plus && plus.closest('[data-line]')) {
          e.preventDefault();
          const lineEl = plus.closest('[data-line]');
          const input = $('[data-qty]', lineEl);
          const next = Math.max(0, parseInt(input.value || '1', 10) + (plus.hasAttribute('data-qty-plus') ? 1 : -1));
          lineEl.classList.add('is-loading');
          try { await this.change(parseInt(lineEl.dataset.line, 10), next); } catch (err) { toast(err.message, 'error'); lineEl.classList.remove('is-loading'); }
          return;
        }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) {
          e.preventDefault();
          const lineEl = remove.closest('[data-line]');
          lineEl.classList.add('is-loading');
          try { await this.change(parseInt(lineEl.dataset.line, 10), 0); } catch (err) { toast(err.message, 'error'); lineEl.classList.remove('is-loading'); }
          return;
        }
        const giftBtn = e.target.closest('[data-gift-add]');
        if (giftBtn) {
          e.preventDefault();
          const row = giftBtn.closest('[data-gift-item]');
          const select = $('[data-gift-variant]', row);
          const id = parseInt(select ? select.value : row.dataset.variantId, 10);
          giftBtn.setAttribute('aria-busy', 'true');
          try {
            const data = await this.post(C.routes.cartAdd, { items: [{ id, quantity: 1, properties: { _gift: 'true' } }] });
            if (data.sections) this.render(data.sections);
            await this.refresh(this.state);
            track('gift_claimed', { variant_id: id });
          } catch (err) { toast(err.message, 'error'); }
          giftBtn.removeAttribute('aria-busy');
          return;
        }
        const upsellAdd = e.target.closest('[data-upsell-add]');
        if (upsellAdd) {
          e.preventDefault();
          const card = upsellAdd.closest('[data-upsell]');
          const select = $('[data-upsell-variant]', card);
          const id = parseInt(select ? select.value : card.dataset.variantId, 10);
          upsellAdd.setAttribute('aria-busy', 'true');
          try { await this.add([{ id, quantity: 1 }], { open: true, source: 'cart_upsell' }); track('upsell_added', { variant_id: id }); } catch (err) { toast(err.message, 'error'); }
          upsellAdd.removeAttribute('aria-busy');
        }
      });
      document.addEventListener('change', async (e) => {
        if (e.target.matches('[data-qty]') && e.target.closest('[data-line]')) {
          const lineEl = e.target.closest('[data-line]');
          lineEl.classList.add('is-loading');
          try { await this.change(parseInt(lineEl.dataset.line, 10), Math.max(0, parseInt(e.target.value || '0', 10))); } catch (err) { toast(err.message, 'error'); lineEl.classList.remove('is-loading'); }
        }
        if (e.target.matches('[data-line-option]')) this.swapVariant(e.target.closest('[data-line]'));
        if (e.target.matches('[data-protection-toggle]')) this.toggleProtection(e.target.checked);
      });
      document.addEventListener('input', debounce((e) => {
        if (e.target.matches('[data-cart-note]')) this.update({ note: e.target.value }).catch(() => {});
      }, 600));
    }
  };

  /* ---------- Carousels ---------- */
  const carouselStep = (track) => { const item = track.children[0]; return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth; };
  function carouselButtons(wrap) {
    const own = $$('[data-carousel-prev], [data-carousel-next]', wrap).filter((b) => !b.dataset.carouselPrev && !b.dataset.carouselNext);
    const linked = wrap.id ? $$(`[data-carousel-prev="${wrap.id}"], [data-carousel-next="${wrap.id}"]`) : [];
    return own.concat(linked);
  }
  function initCarousels(root = document) {
    $$('[data-carousel]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const track = $('[data-carousel-track]', wrap);
      if (!track) return;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        carouselButtons(wrap).forEach((b) => { b.disabled = b.hasAttribute('data-carousel-prev') ? track.scrollLeft <= 2 : track.scrollLeft >= max; });
      };
      track.addEventListener('scroll', debounce(update, 60), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-carousel-prev], [data-carousel-next]');
    if (!btn) return;
    const ref = btn.dataset.carouselPrev || btn.dataset.carouselNext;
    let wrap = ref ? document.getElementById(ref) : btn.closest('[data-carousel]');
    if (wrap && !wrap.hasAttribute('data-carousel')) wrap = $$('[data-carousel]', wrap).find((c) => c.offsetParent !== null);
    const track = wrap && $('[data-carousel-track]', wrap);
    if (!track) return;
    const dir = btn.hasAttribute('data-carousel-prev') ? -1 : 1;
    track.scrollBy({ left: dir * carouselStep(track), behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- Slideshow ---------- */
  function initSlideshows(root = document) {
    $$('[data-slideshow]', root).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const track = $('[data-slideshow-track]', el);
      const slides = $$('.ac-slide', track);
      const dots = $$('[data-slide-dot]', el);
      const pauseBtn = $('[data-slideshow-pause]', el);
      const interval = parseInt(el.dataset.autoplay || '0', 10) * 1000;
      let index = 0;
      let timer = null;
      let paused = reduceMotion || !interval;
      const setActive = (i) => {
        index = i;
        slides.forEach((s, n) => { s.classList.toggle('is-active', n === i); s.setAttribute('aria-hidden', n === i ? 'false' : 'true'); });
        dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      };
      const goTo = (i) => {
        const n = (i + slides.length) % slides.length;
        const slide = slides[n];
        track.scrollTo({ left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
        setActive(n);
      };
      const play = () => { stop(); if (!paused && slides.length > 1) timer = setInterval(() => goTo(index + 1), interval); };
      const stop = () => { if (timer) clearInterval(timer); timer = null; };
      track.addEventListener('scroll', debounce(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let best = 0; let dist = Infinity;
        slides.forEach((s, n) => { const c = s.offsetLeft + s.clientWidth / 2; const d = Math.abs(c - center); if (d < dist) { dist = d; best = n; } });
        setActive(best);
      }, 80), { passive: true });
      dots.forEach((d, n) => d.addEventListener('click', () => { goTo(n); play(); }));
      const prev = $('[data-slide-prev]', el); const next = $('[data-slide-next]', el);
      if (prev) prev.addEventListener('click', () => { goTo(index - 1); play(); });
      if (next) next.addEventListener('click', () => { goTo(index + 1); play(); });
      if (pauseBtn) {
        pauseBtn.addEventListener('click', () => {
          paused = !paused;
          pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
          $('[data-icon-pause]', pauseBtn).hidden = paused;
          $('[data-icon-play]', pauseBtn).hidden = !paused;
          paused ? stop() : play();
        });
        if (paused) { $('[data-icon-pause]', pauseBtn).hidden = true; $('[data-icon-play]', pauseBtn).hidden = false; }
      }
      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', play);
      el.addEventListener('focusin', stop);
      setActive(0);
      play();
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root = document) {
    $$('[data-tabs]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const tabs = $$('[role="tab"]', wrap);
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
            select(n); tabs[n].focus();
          }
        });
      });
      function select(i) {
        tabs.forEach((t, n) => {
          t.setAttribute('aria-selected', n === i ? 'true' : 'false');
          t.tabIndex = n === i ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) { panel.hidden = n !== i; if (n === i) { initCarousels(panel); reveal(panel); } }
        });
      }
    });
  }

  /* ---------- Announcement bar ---------- */
  function initAnnouncement() {
    $$('[data-announcement]').forEach((bar) => {
      const msgs = $$('.ac-announcement__msg', bar);
      if (msgs.length < 2) { if (msgs[0]) msgs[0].classList.add('is-active'); return; }
      let i = 0; let timer;
      const show = (n) => { i = (n + msgs.length) % msgs.length; msgs.forEach((m, k) => { m.classList.toggle('is-active', k === i); m.setAttribute('aria-hidden', k === i ? 'false' : 'true'); }); };
      const play = () => { clearInterval(timer); if (!reduceMotion) timer = setInterval(() => show(i + 1), parseInt(bar.dataset.speed || '5', 10) * 1000); };
      const prev = $('[data-ann-prev]', bar); const next = $('[data-ann-next]', bar);
      if (prev) prev.addEventListener('click', () => { show(i - 1); play(); });
      if (next) next.addEventListener('click', () => { show(i + 1); play(); });
      bar.addEventListener('mouseenter', () => clearInterval(timer));
      bar.addEventListener('mouseleave', play);
      show(0); play();
    });
  }

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Reveal, words, counters ---------- */
  function splitWords(el) {
    if (el.dataset.split) return;
    el.dataset.split = '1';
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const span = document.createElement('span');
            span.className = 'ac-word';
            span.style.setProperty('--i', i++);
            span.textContent = part;
            frag.appendChild(span);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && !child.matches('svg')) walk(child);
      });
    };
    walk(el);
  }

  function countUp(el) {
    if (el.dataset.counted) return;
    el.dataset.counted = '1';
    const target = parseFloat(el.dataset.countTo);
    const decimals = (el.dataset.countTo.split('.')[1] || '').length;
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    const fmt = (n) => prefix + n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
    if (reduceMotion || isNaN(target)) { el.textContent = fmt(target || 0); return; }
    const start = performance.now();
    const dur = 1600;
    (function frame(now) {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = fmt(target * eased);
      if (t < 1) requestAnimationFrame(frame);
    })(start);
  }

  let revealObserver;
  function reveal(root = document) {
    const els = $$('[data-reveal], [data-words], .ac-reveal-ready, [data-count-to]', root);
    if (C.words && !reduceMotion) $$('[data-words]', root).forEach(splitWords);
    if (!C.animations || reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => { el.classList.add('is-revealed'); if (el.dataset.countTo) countUp(el); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          if (entry.target.dataset.countTo) countUp(entry.target);
          $$('[data-count-to]', entry.target).forEach(countUp);
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }
    els.forEach((el) => {
      if (el.classList.contains('is-revealed')) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < innerHeight && rect.bottom > 0 && rect.height === 0) { el.classList.add('is-revealed'); return; }
      revealObserver.observe(el);
    });
  }

  /* ---------- Gallery ---------- */
  class Gallery {
    constructor(root) {
      this.root = root;
      this.track = $('[data-gallery-track]', root);
      this.slides = $$('.ac-gallery__slide', this.track);
      this.thumbs = $$('[data-thumb]', root);
      this.dots = $$('[data-gallery-dots] .ac-dot', root);
      this.index = 0;
      const prev = $('[data-gallery-prev]', root); const next = $('[data-gallery-next]', root);
      if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
      if (next) next.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.track.addEventListener('scroll', debounce(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.mark(Math.max(0, Math.min(this.slides.length - 1, i)));
      }, 60), { passive: true });
    }
    mark(i) {
      this.index = i;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      const thumb = this.thumbs[i];
      if (thumb && thumb.parentElement) thumb.parentElement.scrollTo({ left: thumb.offsetLeft - 40, behavior: 'smooth' });
    }
    go(i) {
      if (!this.slides.length) return;
      const n = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.slides[n].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.mark(n);
    }
    goToMedia(id) {
      if (!id) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i);
    }
  }

  /* ---------- Buy box ---------- */
  /* Shopify rounds a percentage discount down to the cent on each item. */
  const packUnitPrice = (price, percent) => price - Math.floor((price * percent) / 100 + 1e-6);
  class BuyBox {
    constructor(root) {
      this.root = root;
      this.data = JSON.parse($('[data-product-json]', root).textContent);
      this.mode = root.dataset.mode || 'page';
      this.form = $('[data-buy-form]', root);
      this.atc = $('[data-atc]', root);
      this.atcLabel = $('[data-atc-label]', root);
      this.error = $('[data-form-error]', root);
      this.packs = $$('[data-pack]', root);
      this.addon = $('[data-addon]', root);
      this.addonSelect = $('[data-addon-variant]', root);
      this.addonOpts = $('[data-addon-opts]', root);
      this.addonReveal = !!(this.addonOpts && this.addonOpts.hidden);
      /* Set once the shopper picks an option, so offer cards elsewhere know a size was chosen on purpose. */
      this.touched = false;
      this.selected = this.readMain();
      this.qty = this.packs.length ? parseInt(($('[data-pack-input]:checked', root) || {}).value || '1', 10) : 1;
      const scope = root.closest('[data-main-product], .ac-modal, section') || document;
      const galleryEl = $('[data-gallery]', scope);
      this.gallery = galleryEl ? (galleryEl._gallery || (galleryEl._gallery = new Gallery(galleryEl))) : null;
      this.bind();
      this.update();
      this.setupSizeGuide();
    }
    readMain() {
      const opts = [];
      $$('[data-option]:checked', this.root).forEach((input) => { opts[parseInt(input.dataset.option, 10)] = input.value; });
      if (!opts.length && this.data.variants[0]) return this.data.variants[0].options.slice();
      const first = this.data.variants.find((v) => v.available) || this.data.variants[0];
      return this.data.options.map((_, i) => (opts[i] != null ? opts[i] : first.options[i]));
    }
    find(options) { return this.data.variants.find((v) => v.options.every((o, i) => o === options[i])); }
    get variant() { return this.find(this.selected); }
    get pct() { const p = this.data.packs.find((x) => x.qty === this.qty); return p ? parseFloat(p.percent) || 0 : 0; }
    bind() {
      this.root.addEventListener('change', (e) => {
        const t = e.target;
        if (t.matches('[data-option], [data-unit-option]')) {
          this.touched = true;
          this.root.classList.remove('needs-size');
        }
        if (t.matches('[data-option]')) {
          this.selected[parseInt(t.dataset.option, 10)] = t.value;
          const fs = t.closest('[data-option-group]');
          const label = fs && $('[data-option-selected]', fs);
          if (label) label.textContent = t.value;
          this.syncUnits();
          this.markAvailability();
          const v = this.variant;
          if (v && this.gallery) this.gallery.goToMedia(v.media);
          if (v && this.mode === 'page' && history.replaceState) {
            const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url.toString());
          }
          this.update();
        }
        if (t.matches('[data-pack-input]')) { this.selectPack(parseInt(t.value, 10)); }
        if (t.matches('[data-unit-option]')) this.update();
        if (t.matches('[data-addon], [data-addon-variant]')) {
          if (this.addonSelect && this.addon) {
            const o = this.addonSelect.selectedOptions[0];
            this.addon.value = o.value; this.addon.dataset.addonPrice = o.dataset.price; this.addon.dataset.addonFull = o.dataset.full;
          }
          if (this.addonReveal && this.addonOpts) this.addonOpts.hidden = !(this.addon && this.addon.checked);
          if (t.matches('[data-addon]')) track('addon_toggled', { checked: !!(this.addon && this.addon.checked) });
          this.update();
        }
      });
      this.packs.forEach((pack) => pack.addEventListener('click', (e) => {
        if (e.target.closest('select, label')) return;
        const input = $('[data-pack-input]', pack); if (input && !input.checked) { input.checked = true; this.selectPack(parseInt(input.value, 10)); }
      }));
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart(); });
      const buyNow = $('[data-buy-now]', this.root);
      if (buyNow) buyNow.addEventListener('click', () => this.addToCart({ checkout: true, button: buyNow }));
      document.addEventListener('arelio:cart-updated', () => { if (this.state) this.updateShipBar(this.state.total); });
      this.markAvailability();
    }
    selectPack(qty) {
      this.qty = qty;
      this.packs.forEach((p) => p.classList.toggle('is-selected', parseInt(p.dataset.pack, 10) === qty));
      track('pack_selected', { product_id: this.data.id, quantity: qty });
      this.update();
    }
    syncUnits() {
      $$('[data-unit]', this.root).forEach((unit) => {
        $$('[data-unit-option]', unit).forEach((sel) => { const i = parseInt(sel.dataset.unitOption, 10); if (this.selected[i] != null) sel.value = this.selected[i]; });
      });
    }
    markAvailability() {
      $$('[data-option-group]', this.root).forEach((fs) => {
        const idx = parseInt(fs.dataset.optionGroup, 10);
        $$('[data-option]', fs).forEach((input) => {
          const test = this.selected.slice(); test[idx] = input.value;
          const v = this.find(test);
          const wrap = input.closest('.ac-pill, .ac-swatch-input');
          if (wrap) wrap.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    }
    units() {
      const v = this.variant;
      if (this.qty <= 1 || !this.packs.length) return v ? [v] : [];
      const pack = this.packs.find((p) => parseInt(p.dataset.pack, 10) === this.qty);
      const rows = pack ? $$('[data-unit]', pack) : [];
      if (!rows.length) return v ? Array(this.qty).fill(v) : [];
      return rows.map((row) => {
        const opts = this.selected.slice();
        $$('[data-unit-option]', row).forEach((sel) => { opts[parseInt(sel.dataset.unitOption, 10)] = sel.value; });
        return this.find(opts) || null;
      });
    }
    stock(v) {
      if (!v) return { cls: 'ac-stock--out', text: S.unavailable || 'Unavailable' };
      if (!v.available) return { cls: 'ac-stock--out', text: S.soldOut || 'Sold out' };
      if (v.tracked && v.qty != null && v.qty > 0 && v.qty < this.data.lowStock) return { cls: 'ac-stock--low', html: fill(S.lowStock, { count: v.qty }) };
      return { cls: '', text: S.inStock || 'In stock, ready to ship' };
    }
    update() {
      const v = this.variant;
      const units = this.units();
      const pct = this.pct;
      const basePrice = v ? v.price : 0;
      this.packs.forEach((pack) => {
        const qty = parseInt(pack.dataset.pack, 10);
        const p = this.data.packs.find((x) => x.qty === qty);
        const percent = p ? parseFloat(p.percent) || 0 : 0;
        const priced = qty === this.qty && units.every(Boolean) ? units.map((u) => u.price) : Array(qty).fill(basePrice);
        const singles = priced.reduce((sum, price) => sum + price, 0);
        const total = priced.reduce((sum, price) => sum + packUnitPrice(price, percent), 0);
        const totalEl = $('[data-pack-total]', pack); if (totalEl) totalEl.textContent = formatMoney(total); const singlesEl = $('[data-pack-singles]', pack); if (singlesEl) singlesEl.textContent = formatMoney(singles);
        const saveEl = $('[data-pack-save]', pack); if (saveEl) saveEl.textContent = fill(S.save, { amount: formatMoney(singles - total) });
        const eachEl = $('[data-pack-each]', pack); if (eachEl && qty > 1) eachEl.textContent = fill(S.each, { price: formatMoney(Math.round(total / qty)) });
      });
      const pricedSelected = units.length && units.every(Boolean) ? units.map((u) => u.price) : Array(this.qty).fill(basePrice);
      const singlesSelected = pricedSelected.reduce((sum, price) => sum + price, 0);
      /* Mix and match: a ticked add-on counts as one more item toward the pack discount, for every item. */
      const addonOn = !!(this.addon && this.addon.checked);
      const mix = !!this.data.addonMix;
      const tierFor = (n) => this.data.packs.reduce((best, p) => (n >= p.qty ? parseFloat(p.percent) || 0 : best), 0);
      const unitPct = mix && addonOn ? tierFor(this.qty + 1) : pct;
      let total = pricedSelected.reduce((sum, price) => sum + packUnitPrice(price, unitPct), 0);
      let saving = singlesSelected - total;
      if (this.addon) {
        const base = parseInt(this.addon.dataset.addonPrice || '0', 10);
        const addonPct = mix ? tierFor(this.qty + 1) : 0;
        const price = mix ? packUnitPrice(base, addonPct) : base;
        const full = mix ? base : parseInt(this.addon.dataset.addonFull || '0', 10);
        this.renderAddon(price, full, mix ? Math.round(addonPct) : (full > price ? Math.floor(((full - price) * 100) / full) : 0), mix);
        if (addonOn) { total += price; saving += Math.max(0, full - price); }
      }
      const unitPrice = $('[data-unit-price]', this.root);
      if (unitPrice && v) unitPrice.innerHTML = `<span class="ac-price"><span class="ac-price__current">${formatMoney(v.price)}</span></span>`;
      const st = this.stock(v);
      const stockEl = $('[data-stock]', this.root);
      if (stockEl) {
        stockEl.className = 'ac-stock ' + st.cls;
        const txt = $('[data-stock-text]', stockEl);
        if (st.html) txt.innerHTML = st.html; else txt.textContent = st.text;
      }
      const allOk = units.length > 0 && units.every((u) => u && u.available);
      const neededByVariant = {};
      units.forEach((u) => { if (u) neededByVariant[u.id] = (neededByVariant[u.id] || 0) + 1; });
      const stockOk = Object.keys(neededByVariant).every((id) => { const u = this.data.variants.find((x) => String(x.id) === id); return !u.tracked || u.qty == null || u.qty >= neededByVariant[id]; });
      const ok = allOk && stockOk;
      $$('[data-unit]', this.root).forEach((row) => row.classList.remove('is-invalid'));
      if (this.atc) {
        this.atc.disabled = !ok;
        if (this.atcLabel) this.atcLabel.textContent = ok ? `${S.addToCart || 'Add to cart'} · ${formatMoney(total)}` : (v && !v.available ? (S.soldOut || 'Sold out') : (S.unavailable || 'Unavailable'));
      }
      const sav = $('[data-savings]', this.root);
      if (sav) {
        sav.hidden = saving <= 0;
        const txt = $('[data-savings-text]', sav); if (txt) txt.innerHTML = fill(S.savingLine, { amount: formatMoney(saving) });
      }
      const buyNowBtn = $('[data-buy-now]', this.root); if (buyNowBtn) buyNowBtn.disabled = !ok;
      const bar = $('[data-stock-bar]', this.root);
      if (bar) {
        const low = v && v.tracked && v.qty != null && v.qty > 0 && v.qty < this.data.lowStock;
        bar.hidden = !low;
        if (low) {
          bar.style.setProperty('--p', Math.max(8, Math.round((v.qty / this.data.lowStock) * 100)) + '%');
          const t = $('[data-stock-bar-text]', bar); if (t) t.innerHTML = fill(S.lowStock, { count: v.qty });
        }
      }
      this.updateShipBar(total);
      const idInput = $('[data-variant-input]', this.root); if (idInput && v) idInput.value = v.id;
      const qtyInput = $('[data-qty-input]', this.root); if (qtyInput) qtyInput.value = this.qty;
      this.state = { ok, total, saving, qty: this.qty, variant: v };
      this.root.dispatchEvent(new CustomEvent('buybox:update', { bubbles: true, detail: this.state }));
    }
    renderAddon(price, full, percent, mix) {
      const disp = $('[data-addon-display]', this.root); if (disp) disp.textContent = formatMoney(price);
      let was = $('[data-addon-full-display]', this.root);
      if (full > price) {
        if (!was && disp) { was = document.createElement('s'); was.setAttribute('data-addon-full-display', ''); disp.after(was); }
        if (was) { was.hidden = false; was.textContent = formatMoney(full); }
      } else if (was) was.hidden = true;
      const save = $('[data-addon-save]', this.root);
      if (save) { save.hidden = percent < 1; save.textContent = fill(mix ? (S.addonSaveMix || S.addonSave) : S.addonSave, { percent }); }
    }
    items() {
      const map = new Map();
      this.units().forEach((u) => { if (u) map.set(u.id, (map.get(u.id) || 0) + 1); });
      const items = Array.from(map.entries()).map(([id, quantity]) => ({ id, quantity }));
      if (this.addon && this.addon.checked) items.push({ id: parseInt(this.addon.value, 10), quantity: 1, properties: { _addon_for: String(this.data.id) } });
      return items;
    }
    /* "Spend $X more for free shipping": the cart total plus what is selected now. */
    updateShipBar(selectionTotal) {
      const el = $('[data-ship-bar]', this.root);
      if (!el || !C.freeShipping) return;
      const goal = C.freeShipping * Cart.rate();
      const inCart = Cart.state ? Cart.qualifyingTotal(Cart.state) : (parseInt(el.dataset.cartTotal || '0', 10) || 0);
      const sum = inCart + (selectionTotal || 0);
      const left = Math.max(0, goal - sum);
      el.style.setProperty('--p', Math.min(100, Math.round((sum / goal) * 100)) + '%');
      el.classList.toggle('is-done', left === 0);
      const t = $('[data-ship-bar-text]', el);
      if (t) t.innerHTML = left === 0 ? (S.shipDone || 'This order ships free') : fill(S.shipLeft, { amount: formatMoney(left) });
    }
    async addToCart(opts = {}) {
      if (!this.state || !this.state.ok) return;
      const checkout = opts.checkout || this.root.dataset.action === 'checkout';
      const btn = opts.button || this.atc;
      btn.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      try {
        await Cart.add(this.items(), { source: this.mode === 'quick' ? 'quick_view' : (opts.source || 'buy_box'), open: !checkout });
        if (checkout) { track('buy_now', { product_id: this.data.id, quantity: this.qty }); location.href = (C.routes.root || '/').replace(/\/?$/, '/') + 'checkout'; return; }
        if (this.mode === 'quick') { const modal = this.root.closest('.ac-modal'); if (modal) Overlay.close(modal); }
      } catch (e) {
        if (this.error) { this.error.textContent = e.message; this.error.hidden = false; }
        toast(e.message, 'error');
      }
      btn.removeAttribute('aria-busy');
    }
    setupSizeGuide() {
      const btn = $('[data-size-guide]', this.root);
      if (!btn) return;
      const template = $('[data-size-chart-template]', this.root);
      const pageChart = $('[data-size-chart]');
      if (!template && !pageChart) return;
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const modal = $('#SizeGuideModal');
        if (!modal) return;
        const body = $('[data-modal-body]', modal);
        body.innerHTML = '';
        if (template) body.appendChild(template.content.cloneNode(true));
        else body.appendChild(pageChart.cloneNode(true));
        Overlay.open(modal, btn);
      });
    }
  }

  function initBuyBoxes(root = document) {
    $$('[data-buy-box]', root).forEach((el) => { if (!el._buybox) el._buybox = new BuyBox(el); });
    $$('[data-gallery]', root).forEach((el) => { if (!el._gallery) el._gallery = new Gallery(el); });
  }

  /* ---------- Sticky add to cart ---------- */
  function initStickyATC() {
    const bar = $('[data-sticky-atc]');
    const main = $('[data-main-product]');
    if (!bar || !main) return;
    const box = $('[data-buy-box]', main);
    const atc = box && $('[data-atc]', box);
    const btn = $('[data-sticky-button]', bar);
    const summary = $('[data-sticky-summary]', bar);
    const footer = $('.ac-footer');
    let atcVisible = true; let footerVisible = false; let passed = false;
    const sync = () => {
      /* A phone-only bar is display:none on wider screens; then it must not reserve space either. */
      const show = !atcVisible && passed && !footerVisible && !document.body.classList.contains('ac-scroll-lock') && getComputedStyle(bar).display !== 'none';
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      btn.tabIndex = show ? 0 : -1;
      document.body.classList.toggle('has-sticky-atc', show);
      if (show) document.documentElement.style.setProperty('--ac-sticky-h', bar.offsetHeight + 'px');
    };
    /* Measured on every scroll, not with an IntersectionObserver: a jump straight from below the buy box back up to it
       never crosses it, so an observer would not fire and the bar would stay over the packs. The slim bar watches the
       whole buy box; the classic bar watches its add to cart button. */
    const jump = bar.dataset.stickyMode === 'jump';
    const anchorEl = jump ? box : atc;
    const measure = () => {
      if (!anchorEl) return;
      const r = anchorEl.getBoundingClientRect();
      atcVisible = r.bottom > 0 && r.top < window.innerHeight;
      passed = r.bottom <= 0;
      sync();
    };
    let raf;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    measure();
    if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; sync(); }).observe(footer);
    const apply = (st) => {
      if (summary) summary.textContent = st.ok ? formatMoney(st.total) : '';
      if (!jump) btn.disabled = !st.ok;
    };
    if (box) box.addEventListener('buybox:update', (e) => apply(e.detail));
    /* The buy box may have sent its first update before this listener existed. */
    if (box && box._buybox && box._buybox.state) apply(box._buybox.state);
    btn.addEventListener('click', () => {
      if (jump) {
        /* Slim bar: back to the pack choice, so the shopper picks a pack and size first. */
        const target = (box && ($('[data-packs]', box) || $('[data-option-group]', box))) || main;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        track('sticky_jump', {});
        return;
      }
      if (box && box._buybox && box._buybox.state && box._buybox.state.ok) box._buybox.addToCart();
      else main.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ---------- Quick view ---------- */
  const QuickView = {
    cache: new Map(),
    async open(url, trigger) {
      const modal = $('#QuickView');
      if (!modal) { location.href = url; return; }
      const body = $('[data-modal-body]', modal);
      body.innerHTML = '<div class="ac-qv-loading"></div>';
      Overlay.open(modal, trigger);
      try {
        let html = this.cache.get(url);
        if (!html) {
          const u = new URL(url, location.origin);
          u.searchParams.set('section_id', 'ac-quick-view');
          const doc = await fetchHTML(u.toString());
          const inner = $('[data-quick-view-inner]', doc);
          html = inner ? inner.innerHTML : '';
          this.cache.set(url, html);
        }
        body.innerHTML = html;
        initBuyBoxes(body);
        track('quick_view', { url });
      } catch (e) { location.href = url; }
    },
    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-view]');
        if (!btn) return;
        e.preventDefault();
        this.open(btn.getAttribute('data-quick-view'), btn);
      });
    }
  };

  /* ---------- Predictive search ---------- */
  const Search = {
    init() {
      const modal = $('#SearchModal');
      if (!modal) return;
      const input = $('[data-search-input]', modal);
      const results = $('[data-search-results]', modal);
      const initial = results ? results.innerHTML : '';
      if (!C.predictive || !input || !results) return;
      let controller;
      const run = debounce(async () => {
        const q = input.value.trim();
        if (!q) { results.innerHTML = initial; return; }
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const url = `${C.routes.predictiveSearch}.json?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=6&resources[options][unavailable_products]=last`;
          const data = await fetchJSON(url, { signal: controller.signal });
          const r = (data && data.resources && data.resources.results) || {};
          const queries = r.queries || [];
          const products = r.products || [];
          if (!queries.length && !products.length) { results.innerHTML = `<p class="ac-muted">${escapeHtml(fill(S.searchNoResults, { terms: q }))}</p>`; return; }
          const qs = queries.length ? `<div class="ac-search__suggestions"><p class="ac-search__heading">${escapeHtml(S.searchSuggestions || 'Suggestions')}</p>${queries.map((x) => `<a href="${escapeHtml(x.url)}">${x.styled_text || escapeHtml(x.text)}</a>`).join('')}</div>` : '<div></div>';
          const ps = products.length ? `<div><p class="ac-search__heading">${escapeHtml(S.searchProducts || 'Products')}</p><div class="ac-search__products">${products.map((p) => {
            const imgUrl = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
            const img = imgUrl ? `<img src="${escapeHtml(sized(imgUrl, 160))}" alt="" loading="lazy" width="64" height="64">` : '<span></span>';
            const priceCents = Math.round(parseFloat(p.price_min || p.price || 0) * 100);
            return `<a class="ac-search__product" href="${escapeHtml(p.url)}">${img}<span><span class="ac-search__product-title">${escapeHtml(p.title)}</span><br><span class="ac-muted ac-small">${formatMoney(priceCents)}</span></span></a>`;
          }).join('')}</div><a class="ac-btn ac-btn--outline ac-btn--sm ac-search__all" href="${C.routes.search}?q=${encodeURIComponent(q)}&type=product">${escapeHtml(fill(S.searchViewAll, { terms: q }))}</a></div>` : '';
          results.innerHTML = qs + ps;
        } catch (e) {
          /* Live suggestions can fail (for example in a store language Shopify doesn't support); the full search page still works. */
          if (e.name !== 'AbortError') results.innerHTML = `<a class="ac-btn ac-btn--outline ac-btn--sm ac-search__all" href="${C.routes.search}?q=${encodeURIComponent(q)}&type=product">${escapeHtml(fill(S.searchViewAll, { terms: q }))}</a>`;
        }
      }, 220);
      input.addEventListener('input', run);
      modal.addEventListener('overlay:open', () => setTimeout(() => input.focus(), 80));
    }
  };

  /* ---------- Collection filters ---------- */
  const Filters = {
    init() {
      const root = $('[data-collection]');
      if (!root) return;
      const sectionId = root.dataset.sectionId;
      const apply = async (url, push = true) => {
        const grid = $('[data-collection-results]', root);
        if (grid) grid.classList.add('is-loading');
        try {
          const u = new URL(url, location.origin);
          u.searchParams.set('section_id', sectionId);
          const doc = await fetchHTML(u.toString());
          const fresh = $('[data-collection-results]', doc);
          if (fresh && grid) { grid.innerHTML = fresh.innerHTML; grid.classList.remove('is-loading'); }
          const freshCount = $('[data-filter-count]', doc); const count = $('[data-filter-count]', root);
          if (freshCount && count) count.outerHTML = freshCount.outerHTML;
          const freshFilters = $('[data-filters-body]', doc); const filters = $('[data-filters-body]');
          if (freshFilters && filters) { const open = $$('details[open]', filters).map((d) => d.dataset.group); filters.innerHTML = freshFilters.innerHTML; open.forEach((g) => { const d = $(`details[data-group="${g}"]`, filters); if (d) d.open = true; }); initPriceRanges(filters); }
          const clean = new URL(url, location.origin); clean.searchParams.delete('section_id');
          if (push) history.pushState({ filters: true }, '', clean.pathname + clean.search);
          reveal(grid);
        } catch (e) { location.href = url; }
      };
      const formToURL = (form) => {
        const fd = new FormData(form);
        const params = new URLSearchParams();
        for (const [k, v] of fd.entries()) { if (v !== '' && v != null) params.append(k, v); }
        const sort = $('[data-sort]', root); if (sort && sort.value) params.set('sort_by', sort.value);
        return location.pathname + (params.toString() ? '?' + params.toString() : '');
      };
      document.addEventListener('change', (e) => {
        const form = e.target.closest('[data-filters-form]');
        if (form && !e.target.matches('[type="range"]')) apply(formToURL(form));
        if (e.target.matches('[data-sort]')) {
          const f = $('[data-filters-form]');
          if (f) apply(formToURL(f)); else { const u = new URL(location.href); u.searchParams.set('sort_by', e.target.value); apply(u.toString()); }
        }
      });
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-filters-form]');
        if (!form) return;
        e.preventDefault();
        apply(formToURL(form));
        const drawer = form.closest('.ac-drawer'); if (drawer) Overlay.close(drawer);
      });
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (!link) return;
        e.preventDefault();
        apply(link.href);
      });
      window.addEventListener('popstate', () => apply(location.href, false));
      initPriceRanges(document);
    }
  };

  function initPriceRanges(root) {
    $$('[data-price-range]', root).forEach((wrap) => {
      if (wrap.dataset.ready) return;
      wrap.dataset.ready = '1';
      const [minR, maxR] = $$('input[type="range"]', wrap);
      const [minN, maxN] = $$('input[type="number"]', wrap);
      const fillEl = $('.ac-range__fill', wrap);
      const max = parseFloat(maxR.max);
      const paint = () => { if (fillEl) { fillEl.style.left = (minR.value / max * 100) + '%'; fillEl.style.right = (100 - maxR.value / max * 100) + '%'; } };
      const fromRange = () => {
        if (parseFloat(minR.value) > parseFloat(maxR.value)) minR.value = maxR.value;
        minN.value = minR.value > 0 ? minR.value : '';
        maxN.value = parseFloat(maxR.value) < max ? maxR.value : '';
        paint();
      };
      const commit = () => minN.dispatchEvent(new Event('change', { bubbles: true }));
      minR.addEventListener('input', fromRange); maxR.addEventListener('input', fromRange);
      minR.addEventListener('change', commit); maxR.addEventListener('change', commit);
      minN.addEventListener('input', () => { minR.value = minN.value || 0; paint(); });
      maxN.addEventListener('input', () => { maxR.value = maxN.value || max; paint(); });
      paint();
    });
  }

  /* ---------- Lookbook ---------- */
  function initLookbooks() {
    $$('[data-lookbook]').forEach((look) => {
      const spots = $$('[data-hotspot]', look);
      const track = $('[data-carousel-track]', look);
      const cards = track ? Array.from(track.children) : [];
      let closeTimer;
      const select = (i, scroll = true) => {
        spots.forEach((s, n) => s.classList.toggle('is-active', n === i));
        if (scroll && track && cards[i]) track.scrollTo({ left: cards[i].offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      spots.forEach((spot, i) => {
        const y = parseFloat(getComputedStyle(spot).getPropertyValue('--y')) || 0;
        if (y > 58) spot.classList.add('ac-hotspot--above');
        const btn = $('.ac-hotspot__btn', spot);
        const card = $('.ac-hotspot__card', spot);
        const url = spot.dataset.url;
        if (canHover) {
          const openIt = () => { clearTimeout(closeTimer); spots.forEach((s) => s !== spot && s.classList.remove('is-open')); spot.classList.add('is-open'); select(i); };
          const closeIt = () => { closeTimer = setTimeout(() => spot.classList.remove('is-open'), 220); };
          spot.addEventListener('mouseenter', openIt);
          spot.addEventListener('mouseleave', closeIt);
          btn.addEventListener('focus', openIt);
          btn.addEventListener('blur', closeIt);
          if (card) card.addEventListener('click', (e) => { e.preventDefault(); if (url) QuickView.open(url, btn); });
          btn.addEventListener('click', () => { if (url) QuickView.open(url, btn); });
        } else {
          btn.addEventListener('click', () => { select(i); if (url) QuickView.open(url, btn); });
        }
      });
      if (track) track.addEventListener('scroll', debounce(() => {
        let best = 0; let dist = Infinity;
        cards.forEach((c, n) => { const d = Math.abs(c.offsetLeft - track.offsetLeft - track.scrollLeft); if (d < dist) { dist = d; best = n; } });
        select(best, false);
      }, 90), { passive: true });
      select(0, false);
    });
  }

  /* ---------- Popup ---------- */
  const Popup = {
    init() {
      const modal = $('#EmailPopup');
      if (!modal || !C.popup || !C.popup.enabled) return;
      if (window.Shopify && window.Shopify.designMode) return;
      if (store.get('ac_popup_done')) return;
      const until = store.get('ac_popup_closed_until');
      if (until && Date.now() < until) return;
      if (/\/(cart|checkout|account|challenge)/.test(location.pathname)) return;
      const delay = (C.popup.delay || 0) * 1000;
      const scrollNeed = C.popup.scroll || 0;
      let timePassed = delay === 0; let scrolled = scrollNeed === 0; let shown = false;
      const tryShow = () => { if (!shown && timePassed && scrolled && !document.body.classList.contains('ac-scroll-lock')) show(); };
      const show = () => { shown = true; Overlay.open(modal); track('popup_shown', {}); };
      setTimeout(() => { timePassed = true; tryShow(); }, delay);
      const onScroll = () => {
        const h = document.documentElement.scrollHeight - innerHeight;
        if (h <= 0 || (window.scrollY / h) * 100 >= scrollNeed) { scrolled = true; tryShow(); }
      };
      window.addEventListener('scroll', debounce(onScroll, 120), { passive: true });
      onScroll();
      if (C.popup.exitIntent && canHover) {
        document.addEventListener('mouseout', (e) => { if (!e.relatedTarget && e.clientY <= 0 && !shown) show(); });
      }
      const steps = $$('[data-popup-step]', modal);
      const go = (name) => steps.forEach((s) => { s.hidden = s.dataset.popupStep !== name; });
      modal.addEventListener('click', (e) => {
        if (e.target.closest('[data-popup-next]')) { go('email'); setTimeout(() => { const i = $('input[type="email"]', modal); if (i) i.focus(); }, 50); track('popup_claim', {}); }
        if (e.target.closest('[data-popup-decline]')) { store.set('ac_popup_closed_until', Date.now() + (C.popup.cooldown || 3) * 864e5); Overlay.close(modal); }
      });
      modal.addEventListener('overlay:close', () => { if (!store.get('ac_popup_done')) store.set('ac_popup_closed_until', Date.now() + (C.popup.cooldown || 3) * 864e5); });
      const form = $('form', modal);
      if (form) form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = $('button[type="submit"]', form);
        const msg = $('[data-popup-error]', modal);
        btn.setAttribute('aria-busy', 'true');
        if (msg) msg.hidden = true;
        try {
          const res = await fetch(form.action, { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
          if (/challenge/.test(res.url)) { form.submit(); return; }
          if (!res.ok || !/customer_posted=true/.test(res.url)) throw new Error();
          store.set('ac_popup_done', true);
          go('success');
          track('popup_signup', {});
        } catch (err) { if (msg) { msg.textContent = S.popupError || 'Something went wrong. Please try again.'; msg.hidden = false; } }
        btn.removeAttribute('aria-busy');
      });
    }
  };

  /* ---------- Recently viewed ---------- */
  const Recent = {
    key: 'ac_recent',
    save() {
      const main = $('[data-main-product]');
      if (!main) return;
      const handle = main.dataset.productHandle;
      const list = (store.get(this.key) || []).filter((h) => h !== handle);
      list.unshift(handle);
      store.set(this.key, list.slice(0, 12));
    },
    async render() {
      const box = $('[data-recently-viewed]');
      if (!box) return;
      const mainEl = $('[data-main-product]');
      const current = mainEl ? mainEl.dataset.productHandle : null;
      const handles = (store.get(this.key) || []).filter((h) => h && h !== current).slice(0, parseInt(box.dataset.limit || '4', 10));
      if (!handles.length) return;
      const products = (await Promise.all(handles.map((h) => Cart.productJSON(h).catch(() => null)))).filter((p) => p && p.available !== undefined);
      if (!products.length) return;
      const track = $('[data-carousel-track]', box);
      track.innerHTML = products.map((p) => {
        const img = p.featured_image ? `<img class="ac-card__img--primary" src="${escapeHtml(sized(p.featured_image, 600))}" alt="${escapeHtml(p.title)}" loading="lazy" width="600" height="600">` : '';
        const price = p.price_varies ? `<span class="ac-price__from">${escapeHtml(box.dataset.from || 'From')}</span> <span class="ac-price__current">${formatMoney(p.price_min)}</span>` : `<span class="ac-price__current">${formatMoney(p.price)}</span>`;
        return `<article class="ac-card"><a class="ac-card__media" href="${escapeHtml(p.url)}" tabindex="-1" aria-hidden="true">${img}</a><button type="button" class="ac-card__quick" data-quick-view="${escapeHtml(p.url)}" aria-label="${escapeHtml(p.title)}"><svg class="ac-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg></button><div class="ac-card__body"><h3 class="ac-card__title"><a href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a></h3><span class="ac-price">${price}</span></div></article>`;
      }).join('');
      box.hidden = false;
      initCarousels(box);
    }
  };

  /* ---------- Product recommendations ---------- */
  function initRecommendations() {
    const els = $$('[data-recommendations]');
    if (!els.length) return;
    const load = async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      try {
        const doc = await fetchHTML(el.dataset.url);
        const inner = $('[data-recommendations-inner]', doc);
        if (inner) { el.innerHTML = inner.outerHTML; initCarousels(el); reveal(el); }
      } catch (e) {}
    };
    if (!('IntersectionObserver' in window)) { els.forEach(load); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { io.unobserve(en.target); load(en.target); } }), { rootMargin: '400px 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Advertorial sticky CTA ---------- */
  function initAdvSticky() {
    const bar = $('[data-adv-sticky]');
    const start = $('[data-adv-start]');
    if (!bar || !start) return;
    new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', e.boundingClientRect.top < 0 && !e.isIntersecting)).observe(start);
  }

  /* ---------- Boot ---------- */
  function boot() {
    UTM.capture();
    Overlay.init();
    Cart.init();
    QuickView.init();
    Search.init();
    initHeader();
    initAnnouncement();
    initCarousels();
    initSlideshows();
    initTabs();
    initBuyBoxes();
    initStickyATC();
    Filters.init();
    initLookbooks();
    Popup.init();
    Recent.save();
    Recent.render();
    initAdvSticky();
    initRecommendations();
    reveal();
    setTimeout(() => $$('[data-reveal]:not(.is-revealed)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < innerHeight) el.classList.add('is-revealed'); }), 2500);
    document.addEventListener('shopify:section:load', (e) => { initCarousels(e.target); initSlideshows(e.target); initTabs(e.target); initBuyBoxes(e.target); initLookbooks(); reveal(e.target); });
  }

  window.Arelio = { Cart, Overlay, QuickView, formatMoney, toast, track };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
